import { v4 as uuid } from 'uuid';

import {
    Config,
    fetchJSON,
    getExperimentSegment,
    ExperimentSegments,
} from '@ratehub/base-ui';
import {
    INTENDED_USE,
    getIsOwnerOccupiedFromIntendedUse,
} from '@ratehub/documents/mtg-journey-document/stable';

import EXPERIMENTS from '../definitions/experiments';
import SCENARIO_TYPES from '../definitions/scenarioTypes';
import getScenarioUrlSlugFromType from './getScenarioUrlSlugFromType';
import normalizeRates from './normalizeRates';


/**
 *
 * @param {string} scenarioType
 * @param {object} scenarioInputs
 */
async function fetchAllRates(scenarioType, params, { callerName = 'fetchAllRates' } = {}) {
    // Remove Experiment MTG-3796
    const isMortgageReformEnabled = getExperimentSegment(EXPERIMENTS.gocMortgageReform) === ExperimentSegments.VARIANT_1;
    const apiParams = whitelistRateParams(scenarioType, getMappedParams(params));
    const fetchedRates = await fetchJSON({
        url: `${Config.MORTGAGE_RATES_API_URL}/all/${getScenarioUrlSlugFromType(scenarioType)}-rates`,
        params: {
            ...apiParams,
            // Adding request_id so we can track this call in BE logs
            request_id: uuid(),

            // TODO: Remove this once the API is updated (EVR-2313) to handle not passing isPreApproval
            isPreApproval: 0,
            ...(isMortgageReformEnabled && {
                mortgageReform: 1,
            }),
        },
        arrayFormat: 'bracket',
        rateLimitRefetch: true,
    }, callerName);

    return normalizeRates(fetchedRates.data);
}

/**
 * Parameters to adapt into API expected types
 * @param {Object} mappedParams
 * @property {boolean} params.isOwnerOccupied
 * @property {boolean} params.intendedUse // Used by the FE to choose owner occupancy
 * @property {boolean} params.isInsured
 * @property {boolean} params.isCashBack
 * @property {boolean} params.isOpen
 */
function getMappedParams(params) {
    const mappedParams = { ...params }; // avoid mutating params

    // Currently throughout the frontend, many of the parameters sent to the rates API
    // are transformed to satisfy the type it expects.
    // This attempts to convert params to API expected values so frontend doesn't have to
    const isOwnerOccupied = mappedParams.intendedUse && Object.values(INTENDED_USE).includes(mappedParams.intendedUse)
        ? getIsOwnerOccupiedFromIntendedUse(mappedParams.intendedUse)
        : mappedParams.isOwnerOccupied;

    if (typeof mappedParams.city === 'object') {
        const city = mappedParams.city;

        mappedParams.city = city.name;
        mappedParams.province = city.province.code;
    }

    if (typeof mappedParams.location === 'object') {
        const city = mappedParams.location;

        mappedParams.city = city.name;
        mappedParams.province = city.province.code;
    }

    return {
        ...mappedParams,
        ...(isOwnerOccupied != null && { isOwnerOccupied: ~~(isOwnerOccupied) }),
        ...(mappedParams.isInsured != null && { isInsured: ~~(mappedParams.isInsured) }),
        ...(mappedParams.isCashBack != null && { isCashBack: ~~(mappedParams.isCashBack) }),
        ...(mappedParams.isOpen != null && { isOpen: ~~(mappedParams.isOpen) }),
    };
}

const VALID_RATE_PARAM_COMMON_KEYS = [
    'province',
    'city',
    'providers',
    'providerType',
    'lenderType',
    'brokerageType',
    'language',
    'isFeatured',
    'isBigBank',
    'date',
];

const VALID_RATE_PARAMS_NON_HELOC_KEYS = [
    'amortization',
    'term',
    'type',
    'isOwnerOccupied',
    'isInsured',
    'isCashBack',
    'isOpen',
];

// We want to make sure we only send valid parameters to the API.
// fetchAllRates accepts a few extra keys (eg. intendedUse) that are not valid API params.
function whitelistRateParams(scenarioType, params) {
    switch (scenarioType) {
        case SCENARIO_TYPES.PURCHASE:
        case SCENARIO_TYPES.PRE_APPROVAL:
            return whitelistProperties(params, [ ...VALID_RATE_PARAM_COMMON_KEYS, ...VALID_RATE_PARAMS_NON_HELOC_KEYS, 'homePrice', 'downPaymentPercent' ]);
        case SCENARIO_TYPES.RENEWAL:
        case SCENARIO_TYPES.REFINANCE:
            return whitelistProperties(params, [ ...VALID_RATE_PARAM_COMMON_KEYS, ...VALID_RATE_PARAMS_NON_HELOC_KEYS ]);
        case SCENARIO_TYPES.HELOC:
            return whitelistProperties(params, VALID_RATE_PARAM_COMMON_KEYS);
        default: throw new Error(`[fetchAllRates]: Unexpected scenario type: "${scenarioType}`);
    }
}

function whitelistProperties(object, whitelist) {
    return Object.keys(object)
        .filter(key => whitelist.includes(key))
        .reduce((obj, key) => {
            obj[key] = object[key];
            return obj;
        }, {});
}

export default fetchAllRates;
